/**
 * Display helpers for the payments pages (docs/Decisions.md D44). Kept out of
 * lib/payments.js so that file stays an exact mirror of the API's rules.
 */

export const FREQUENCY_LABELS = {
  one_time: 'One time',
  month: 'Monthly',
  year: 'Yearly',
};

export const METHOD_LABELS = {
  card: 'Card',
  us_bank_account: 'Bank account (ACH)',
  link: 'Link',
};

export const METHOD_HINTS = {
  card: 'Includes Apple Pay and Google Pay where Stripe offers them.',
  us_bank_account: 'Lower processing cost; the payment takes a few business days to clear.',
  link: "Stripe's saved-details checkout, offered alongside card.",
};

export const CONNECT_STATES = {
  not_connected: {
    label: 'Not connected',
    tone: 'neutral',
    text: 'Connect a Stripe account so donations are paid to your organization.',
  },
  onboarding_incomplete: {
    label: 'Onboarding incomplete',
    tone: 'warning',
    text: 'Finish the Stripe onboarding form to start taking donations.',
  },
  pending_verification: {
    label: 'Verifying',
    tone: 'info',
    text: 'Stripe is verifying your details. This usually takes a few minutes to a few days.',
  },
  restricted: {
    label: 'Restricted',
    tone: 'danger',
    text: 'Stripe needs more information before it can take payments. Open Stripe to resolve it.',
  },
  action_needed: {
    label: 'Action needed',
    tone: 'warning',
    text: 'Payments work, but Stripe needs more information to keep payouts running.',
  },
  active: {
    label: 'Active',
    tone: 'success',
    text: 'Your organization can take donations and receive payouts.',
  },
};

/**
 * Parse a dollars-and-cents string ("25", "25.5", "1,000.00") to integer
 * cents, or null when it is not a valid amount.
 */
export function parseDollarsToCents(text) {
  const cleaned = String(text ?? '').replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole, fraction = ''] = cleaned.split('.');
  return Number(whole) * 100 + Number((fraction + '00').slice(0, 2));
}

/** Integer cents as an input value: 2500 -> "25.00". */
export function centsToInput(cents) {
  if (!Number.isInteger(cents)) return '';
  return (cents / 100).toFixed(2);
}

/** Parse a percentage string to a number with at most two decimals, or null. */
export function parsePercent(text) {
  const cleaned = String(text ?? '').trim();
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100) / 100;
}

const RECIPIENT = {
  platform: 'to the platform',
  stripe: 'to Stripe',
  organization: 'kept by the organization',
};

/**
 * One line of a breakdown's `disclosure` as the donor reads it
 * (docs/Decisions.md D60): "Platform fee (3.5 percent of the donation, to
 * the platform)".
 */
export function disclosureLabel(line) {
  const parts = [];
  if (Number.isFinite(line.percent)) parts.push(`${line.percent} percent of the donation`);
  if (line.key !== 'org_work') parts.push(RECIPIENT[line.recipient] || line.recipient);
  return parts.length ? `${line.label} (${parts.join(', ')})` : line.label;
}
